import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { handleAzureRedirect, isAzureConfigured } from "modules/auth/api/azureAuth"
import { loginWithAzureToken, resolveContractorAzureOnboarding } from "modules/auth/api/authService"

const AZURE_USER_TYPE = "CONTRACTOR"
const AZURE_CONTRACTOR_IDTOKEN_KEY = "eptw.contractor.azure.idToken"
const DEBUG_AZURE_LOGS = process.env.REACT_APP_DEBUG_AZURE === "true"

const log = (message: string, ...args: any[]) => {
  if (DEBUG_AZURE_LOGS) console.info(`[Azure][Contractor] ${message}`, ...args)
}

const setStoredContractorAzureIdToken = (token: string) => {
  try {
    sessionStorage.setItem(AZURE_CONTRACTOR_IDTOKEN_KEY, token)
  } catch {
    // ignore storage failures
  }
}

const getStoredContractorAzureIdToken = () => {
  try {
    return sessionStorage.getItem(AZURE_CONTRACTOR_IDTOKEN_KEY) ?? ""
  } catch {
    return ""
  }
}

const clearStoredContractorAzureIdToken = () => {
  try {
    sessionStorage.removeItem(AZURE_CONTRACTOR_IDTOKEN_KEY)
  } catch {
    // ignore storage failures
  }
}

export function ContractorAzureCallbackPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const callbackStartedAt = performance.now()
      log("callback page started")
      if (!(await isAzureConfigured(AZURE_USER_TYPE))) {
        log("configuration check failed before redirect handling")
        setError("Azure configuration is missing.")
        return
      }

      try {
        const redirectStartedAt = performance.now()
        log("handleAzureRedirect started")
        const result = await handleAzureRedirect(AZURE_USER_TYPE)
        log("handleAzureRedirect finished in", Math.round(performance.now() - redirectStartedAt), "ms")
        if (!result?.idToken) {
          setError("ไม่พบข้อมูลการยืนยันตัวตนจาก Azure")
          return
        }

        const idToken = result.idToken
        setStoredContractorAzureIdToken(idToken)

        const loginStartedAt = performance.now()
        log("loginWithAzureToken started")
        const response = await loginWithAzureToken({
          idToken,
          userType: AZURE_USER_TYPE,
        })
        log("loginWithAzureToken finished in", Math.round(performance.now() - loginStartedAt), "ms")

        if (cancelled) return
        clearStoredContractorAzureIdToken()
        log("callback flow completed in", Math.round(performance.now() - callbackStartedAt), "ms")
        navigate("/modules", { replace: true, state: { user: { ...response, userType: "contractor" } } })
      } catch (err: any) {
        if (cancelled) return

        const idToken = getStoredContractorAzureIdToken()
        const shouldTryOnboarding = Boolean(idToken)

        if (shouldTryOnboarding) {
          try {
            const onboardingStartedAt = performance.now()
            log("resolveContractorAzureOnboarding started")
            const onboardingState = await resolveContractorAzureOnboarding({ idToken })
            log("resolveContractorAzureOnboarding finished in", Math.round(performance.now() - onboardingStartedAt), "ms")
            if (cancelled) return
            const status = (onboardingState?.status ?? "").toUpperCase()
            if (status === "WAITING" || status === "NOT_REGISTERED") {
              navigate("/login/contractor/azure/onboarding", { replace: true })
              return
            }
          } catch {
            // fall through to show main error
          }
        }

        const message = err?.response?.data ?? err?.message ?? "ไม่สามารถเข้าสู่ระบบด้วย Azure ได้"
        setError(typeof message === "string" ? message : "ไม่สามารถเข้าสู่ระบบด้วย Azure ได้")
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-orange-100 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white/90 shadow-xl border border-orange-100 p-6 text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-red-600">Azure Login Failed</h1>
            <p className="mt-2 text-sm text-red-500">{error}</p>
            <button
              type="button"
              className="mt-4 text-sm text-orange-600 hover:underline"
              onClick={() => {
                clearStoredContractorAzureIdToken()
                navigate("/login/contractor")
              }}
            >
              Back to Contractor Login
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-orange-400 border-t-transparent" />
            <h1 className="text-lg font-semibold text-orange-900">Signing you in...</h1>
            <p className="mt-2 text-sm text-orange-700">Please wait while we validate your account.</p>
          </>
        )}
      </div>
    </div>
  )
}

export default ContractorAzureCallbackPage
